<!-- eslint-disable vue/require-v-for-key -->
<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      Upload Barge Line Up
    </p>
    <div class="config-container">
      <p class="self-center font-semibold text-xs lg:text-lg">
        Configuration for Product Columns
      </p>
      <div
        v-show="isUserCanAccessMenu(permission.name, 'add')"
        class="flex flex-row ml-auto"
      >
        <vs-input
          v-model="columnStart"
          placeholder="Column start"
        />
        <p class="mx-2 self-center">
          -
        </p>
        <vs-input
          v-model="columnEnd"
          placeholder="Column end"
        />
        <vs-button
          ref="saveConfigButton"
          color="primary"
          class="ml-5 mr-1 vs-con-loading__container"
          :disabled="isProcessing"
          @click="saveColumnConfig"
        >
          <span class="text-xs lg:text-sm">Save Configuration</span>
        </vs-button>
        <vs-button
          type="border"
          color="primary"
          class="ml-1"
          :disabled="isProcessing"
          @click="resetColumns"
        >
          Reset
        </vs-button>
      </div>
    </div>
    <upload-file
      v-show="isUserCanAccessMenu(permission.name, 'add')"
      :auto-preview="true"
      class="mb-3"
      :loading="isLoading"
      @upload="handleUploadFile"
      @remove="resetData"
    />
    <vs-progress
      indeterminate
      v-show="isTableLoading"
      color="secondary"
      class="px-5"
    />
    <div v-if="tableData.length">
      <p class="mb-2 font-medium">
        Result
      </p>
      <vs-collapse
        :class="comIsError ? 'text-alert-danger' : 'text-alert-success'"
        type="margin"
      >
        <vs-collapse-item
          v-if="!comIsError"
          class="collapse-container"
        >
          <div
            slot="header"
            class="flex items-center gap-3"
          >
            <span class="material-icons-outlined">
              check_circle
            </span>
            Your data looks good and ready to be uploaded!
          </div>

          <div
            v-if="comErrorUnsynchronizedVLU.length"
            class="flex items-center gap-3"
          >
            <div>
              <span class="material-icons">
                report
              </span>
            </div>
            <div>
              Some entries contain unsynchronized <span class="font-semibold">Vessel and End User</span> names.
              The entries have been disabled and cannot be submitted -
              <span
                class="cursor-pointer font-semibold"
                v-for="value in comErrorUnsynchronizedVLU"
                @click="goToRowTable(value)"
              > Row {{ value }},</span>
            </div>
          </div>

          <div
            v-for="value in comError"
            :key="value.row"
          >
            <span
              class="cursor-pointer font-semibold"
              @click="goToRowTable(value.row)"
            > Row {{ value.row }}</span> - {{ value.warning }}
          </div>
        </vs-collapse-item>
        <vs-collapse-item
          v-else
          class="collapse-container"
        >
          <div
            slot="header"
            class="flex items-center gap-3"
          >
            <span class="material-icons-outlined">
              cancel
            </span>
            Your data has some invalid values. Please fix the data before submitting.
          </div>

          <div
            v-if="comErrorUnsynchronizedVLU.length"
            class="flex items-center gap-3"
          >
            <div>
              <span class="material-icons">
                report
              </span>
            </div>
            <div>
              Some entries contain unsynchronized <span class="font-semibold">Vessel and End User</span> names.
              The entries have been disabled and cannot be submitted -
              <span
                class="cursor-pointer font-semibold hover:bg-white"
                v-for="value in comErrorUnsynchronizedVLU"
                @click="goToRowTable(value)"
              > Row {{ value }},</span>
            </div>
          </div>
          <div
            v-if="comErrorCompletedVLU.length"
            class="flex items-center gap-3 mt-2"
          >
            <div>
              <span class="material-icons">
                report
              </span>
            </div>
            <div>
              Some entries contain completed VLU. The entries have been disabled and cannot be submitted -
              <span
                class="cursor-pointer font-semibold hover:bg-white"
                v-for="value in comErrorCompletedVLU"
                @click="goToRowTable(value)"
              > Row {{ value }},</span>
            </div>
          </div>
          <div
            v-if="comErrorCombinationOngoing.length"
            class="flex items-center gap-3 mt-2"
          >
            <div>
              <span class="material-icons">
                report
              </span>
            </div>
            <div>
              Some entries contain the same tug, barge, and loading date on ongoing BLU.
              The entries have been disabled and cannot be submitted -
              <span
                class="cursor-pointer font-semibold hover:bg-white"
                v-for="value in comErrorCombinationOngoing"
                @click="goToRowTable(value)"
              > Row {{ value }},</span>
            </div>
          </div>
          
          <div
            v-for="value in comError"
            :key="value.row"
            class="mt-2"
          >
            <span
              class="cursor-pointer font-semibold hover:bg-white"
              @click="goToRowTable(value.row)"
            > Row {{ value.row }}</span> - {{ value.error.length ? value.error : '' }}{{ value.warning ? `, ${value.warning}` : '' }}
          </div>
        </vs-collapse-item>
      </vs-collapse>
    </div>
    <table-top
      ref="tabletop"
      is-search-has-border
      :page-size-start="false"
      class="my-5"
      @fetch="changePageSize"
      :hide-search="!tableData.length"
      :hide-page-limit-selection="!tableData.length"
    >
      <template #start>
        <vs-button
          ref="submitButton"
          class="button shadow-primary vs-con-loading__container"
          color="primary"
          @click="onHandleGenerate"
          :disabled="comIsError || isProcessing || !tableData.length"
        >
          Submit BLU
        </vs-button>
      </template>
    </table-top>
    <data-table
      class="h-full"
      :loading="isTableLoading"
      :table-header="tableHeaders"
      :table-data="paginatedData"
      :grouped-headers="groupHeader"
      :row-class="rowClass"
      body-height="100vh"
      key-header-checkbox="master_checkbox"
      :is-header-checkbox-disabled="isProcessing"
      @checkAll="onHandleSelectAll"
    >
      <template #master_checkbox="{ row }">
        <div class="flex flex-row w-min items-center gap-3">
          <vs-checkbox
            color="primary"
            v-model="row.is_checked"
            :disabled="hasDisableRow(row)"
            @input="(val) => {
              if (val) {
                if (!selectedRow.includes(row.row)) {
                  selectedRow.push(row.row)
                }
              } else {
                selectedRow = selectedRow.filter(r => r !== row.row)
              }
            }"
          />
        </div>
      </template>
      <template #no="{ row, index }">
        <div class="flex flex-row w-min items-center gap-3">
          {{ (currentPage - 1) * pageSize + index + 1 }}
          <div v-if="row.isWarning && !hasDisableRow(row)">
            <span class="material-icons text-danger">
              report
            </span>
          </div>
        </div>
      </template>
      <template #action="{ row }">
        <div class="flex flex-row gap-x-3">
          <template v-if="row.isEditing">
            <div
              class="mx-1 self-center flex items-center justify-center icon-transparent-bg complete"
              @click="onHandleValidateRow(row)"
            >
              <img :src="require('@/assets/shipo-icon-check-circle.svg')">
            </div>
            <div
              class="mx-1 self-center flex items-center justify-center icon-transparent-bg delete"
              @click="onHandleCancel(row)"
            >
              <img :src="require('@/assets/shipo-icon-close-circle.svg')">
            </div>
          </template>
          <template v-else>
            <div
              v-if="isUserCanAccessMenu(permission.name, 'edit')"
              class="flex items-center justify-center icon-transparent-bg"
              @click="onHandleEdit(row)"
            >
              <img :src="require('@/assets/shipo-icon-edit.svg')">
            </div>
          </template>
        </div>
      </template>
      <template #name_of_vessel="{ row }">
        <div>
          {{ validateValue(row.name_of_vessel) }}
        </div>
      </template>
      <template #end_user="{ row }">
        <div>
          {{ validateValue(row.end_user) }}
        </div>
      </template>
      <template #term="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            hide-search
            :clearable="false"
            v-model="row.editForm.term.value"
            placeholder="Select term"
            class="px-2"
            @input="onTermChange(row)"
            :options="termOptions"
            :is-valid="!row.editForm.term.is_error && !row.editForm.term.is_warning"
          />
        </div>
        <div v-else>
          {{ validateValue(row.term) }}
        </div>
      </template>
      <template #tug="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            :clearable="false"
            v-model="row.editForm.tug.value"
            placeholder="Select tug"
            class="px-2"
            :options="tugOptions"
            :is-valid="!row.editForm.tug.is_error && !row.editForm.tug.is_warning"
            :is-loading-refresh="isLoadingRefresh"
            @refresh="onHandleRefresh('TUG', row)"
            @link="onHandleLink('MasterDataTugManagementList')"
            @input="onRelationChange(row, 'tug')"
          />
        </div>
        <div v-else>
          {{ validateValue(row.tug) }}
        </div>
      </template>
      <template #barge="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            :clearable="false"
            v-model="row.editForm.barge.value"
            placeholder="Select barge"
            class="px-2"
            :options="bargeOptions"
            :is-valid="!row.editForm.barge.is_error && !row.editForm.barge.is_warning"
            :is-loading-refresh="isLoadingRefresh"
            @refresh="onHandleRefresh('BARGE', row)"
            @link="onHandleLink('MasterDataBargeManagementList')"
            @input="onRelationChange(row, 'barge')"
          />
        </div>
        <div v-else>
          {{ validateValue(row.barge) }}
        </div>
      </template>
      <template #fleet_id="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            v-model="row.editForm.fleet_id.value"
            placeholder="Select fleet id"
            class="px-2"
            :options="fleetOptions"
            :is-loading-refresh="isLoadingRefresh"
            :is-valid="!row.editForm.fleet_id.is_error && !row.editForm.fleet_id.is_warning"
            @refresh="onHandleRefresh('FLEET_ID', row)"
            @link="onHandleLink('MasterDataTugBargesList')"
            @input="onRelationChange(row, 'fleet_id')"
          />
        </div>
        <div v-else>
          {{ validateValue(row.fleet_id) }}
        </div>
      </template>
      <template #route="{ row }">
        <div v-if="row.isEditing">
          <vs-input
            v-model="row.editForm.route.value"
            placeholder="Enter pol/pod"
            disabled
          />
        </div>
        <div v-else>
          {{ validateValue(row.route) }}
        </div>
      </template>
      <template #loading_date="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="date"
            format="DD-MMM-YYYY"
            v-model="row.editForm.loading_date.value"
            :class="{ 'is-not-valid': row.editForm.loading_date.is_error || row.editForm.loading_date.is_warning }"
            :clearable="false"
            @input="() => {
              row.editForm.loading_date.is_error = !row.editForm.loading_date.value
              row.row.editForm.loading_date.is_warning = !row.editForm.loading_date.value
            }"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.loading_date.value, 'DD-MMM-YYYY') }}
        </div>
      </template>
      <template #max_cargo="{ row }">
        <div v-if="row.isEditing">
          <v-autonumeric
            v-model="row.editForm.max_cargo.value"
            class="vs-input--input w-40"
            :options="globalNumericOptions"
          />
        </div>
        <div v-else>
          {{ handleValidateAndFormatNumber(row.max_cargo.value, 2, '-') }}
        </div>
      </template>
      <template #grt="{ row }">
        <div v-if="row.isEditing">
          <v-autonumeric
            v-model="row.editForm.grt.value"
            class="vs-input--input w-40"
            :options="globalNumericOptions"
          />
        </div>
        <div v-else>
          {{ handleValidateAndFormatNumber(row.grt.value, 0, '-') }}
        </div>
      </template>
      <template #pol="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            :clearable="false"
            v-model="row.editForm.pol.value"
            placeholder="Select pol"
            class="px-2"
            :options="polOptions"
            :is-valid="!row.editForm.pol.is_error && !row.editForm.pol.is_warning"
            :is-loading-refresh="isLoadingRefresh"
            @refresh="onHandleRefresh('POL', row)"
            @link="onHandleLink('LocationManagementList')"
            @input="onPolPodChange(row)"
          />
        </div>
        <div v-else>
          {{ validateValue(row.pol) }}
        </div>
      </template>
      <template #pod="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            :clearable="false"
            v-model="row.editForm.pod.value"
            placeholder="Select pod"
            class="px-2"
            :options="podOptions"
            :is-valid="!row.editForm.pod.is_error && !row.editForm.pod.is_warning"
            :is-loading-refresh="isLoadingRefresh"
            @refresh="onHandleRefresh('POD', row)"
            @link="onHandleLink('LocationManagementList')"
            @input="onPolPodChange(row)"
          />
        </div>
        <div v-else>
          {{ validateValue(row.pod) }}
        </div>
      </template>
      <template #shipper_name="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            is-need-refresh
            is-need-button-link
            :clearable="false"
            v-model="row.editForm.shipper_name.value"
            placeholder="Select shipper name"
            class="px-2"
            :options="shipperOptions"
            :is-valid="!row.editForm.shipper_name.is_error && !row.editForm.shipper_name.is_warning"
            :is-loading-refresh="isLoadingRefresh"
            @refresh="onHandleRefresh('SHIPPER', row)"
            @link="onHandleLink('MasterDataOwnerManagementList', 'site_owner')"
            @input="() => {
              row.editForm.shipper_name.is_error = !row.editForm.shipper_name.value
              row.editForm.shipper_name.is_warning = !row.editForm.shipper_name.value
            }"
          />
        </div>
        <div v-else>
          {{ validateValue(row.shipper_name) }}
        </div>
      </template>
      <template #type_export="{ row }">
        <div v-if="row.isEditing">
          <select-with-search
            hide-search
            :clearable="false"
            v-model="row.editForm.type_export.value"
            placeholder="Select options"
            class="px-2"
            :options="typeExportOptions"
          />
        </div>
        <div v-else>
          {{ validateValue(row.type_export) }}
        </div>
      </template>
      <template #trip_number="{ row }">
        <div v-if="row.isEditing">
          <vs-input
            v-model="row.editForm.trip_number.value"
            placeholder="Enter trip number"
            :color="row.editForm.trip_number.is_error || row.editForm.trip_number.is_warning ? 'danger' : ''"
            @change="() => {
              row.editForm.trip_number.is_error = populateTripNumberError(row)
              row.editForm.trip_number.is_warning = !row.editForm.trip_number.value
            }"
          />
        </div>
        <div v-else>
          {{ validateValue(row.trip_number) }}
        </div>
      </template>
      <template #product_id="{ row }">
        <div v-if="row.isEditing">
          <vs-input
            v-model="row.editForm.product_id.value"
            placeholder="Enter product id"
            :color="row.editForm.product_id.is_error || row.editForm.product_id.is_warning ? 'danger' : ''"
            @change="() => {
              row.editForm.product_id.is_error = !row.editForm.product_id.value
              row.editForm.product_id.is_warning = !row.editForm.product_id.value
            }"
          />
        </div>
        <div v-else>
          {{ validateValue(row.product_id) }}
        </div>
      </template>
      <template #cargo_demand="{ row }">
        <div v-if="row.isEditing">
          <v-autonumeric
            v-model="row.editForm.cargo_demand.value"
            class="autonumeric-primary w-40"
            :class="{ 'autonumeric-danger': row.editForm.cargo_demand.is_error || row.editForm.cargo_demand.is_warning }"
            :options="globalNumericOptions"
            @change="() => {
              row.editForm.cargo_demand.is_error = !row.editForm.cargo_demand.value
              row.editForm.cargo_demand.is_warning = !row.editForm.cargo_demand.value
            }"
          />
        </div>
        <div v-else>
          {{ handleValidateAndFormatNumber(row.cargo_demand.value, 2, '-') }}
        </div>
      </template>
      <!-- Map Dynamic Product Data -->
      <template
        v-for="item in productKeyList"
        #[`${item.key}`]="{ row }"
      >
        <div v-if="row.isEditing">
          <v-autonumeric
            v-model="row.editForm.product_data[`${item.label}`].value"
            class="autonumeric-primary w-40"
            :class="{ 
              'autonumeric-danger': 
                row.editForm.product_data[`${item.label}`].value === null 
                || row.editForm.product_data[`${item.label}`].value < 0
            }"
            :options="globalNumericOptions"
          />
        </div>
        <div v-else>
          {{ handleValidateAndFormatNumber(row.product_data[`${item.label}`].value, 0, '') }}
        </div>
      </template>
      <template #trip_start="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.trip_start.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.trip_start.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #trip_end="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.trip_end.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.trip_end.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #barge_cycle_days="{ row }">
        <div v-if="row.isEditing">
          <v-autonumeric
            v-model="row.editForm.barge_cycle_days.value"
            class="autonumeric-primary w-40"
            :options="globalNumericOptions"
          />
        </div>
        <div v-else>
          {{ validateValue(row.barge_cycle_days) }}
        </div>
      </template>
      <template #arrive_smds="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.arrive_smds.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.arrive_smds.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #depart_smds="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.depart_smds.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.depart_smds.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #arrived_pol="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.arrived_pol.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.arrived_pol.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #loading_start="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.loading_start.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.loading_start.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #loading_end="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.loading_end.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.loading_end.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #cast_off="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.cast_off.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.cast_off.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #departure="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.departure.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.departure.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #smd_for_bridge="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.smd_for_bridge.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.smd_for_bridge.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #open_bridge="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.open_bridge.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.open_bridge.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #actual_passing_bridge="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.actual_passing_bridge.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.actual_passing_bridge.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #arrived_pod="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.arrived_pod.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.arrived_pod.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #unloading_start="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.unloading_start.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.unloading_start.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #unloading_end="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.unloading_end.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.unloading_end.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #cast_off_2="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.cast_off_2.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.cast_off_2.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #departure_2="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.departure_2.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.departure_2.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #arrive_smdr="{ row }">
        <div v-if="row.isEditing">
          <date-picker
            class="w-full"
            type="datetime"
            format="DD-MMM-YYYY HH:mm"
            v-model="row.editForm.arrive_smdr.value"
          />
        </div>
        <div v-else>
          {{ validateAndFormatDate(row.arrive_smdr.value, 'DD-MMM-YYYY HH:mm') }}
        </div>
      </template>
      <template #remark="{ row }">
        <div v-if="row.isEditing">
          <vs-input
            v-model="row.editForm.remark.value"
            placeholder="Enter remark"
          />
        </div>
        <div v-else>
          {{ validateValue(row.remark) }}
        </div>
      </template>
    </data-table>
    <table-bottom
      ref="pagination"
      class="mt-6"
      @fetch="''"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import UploadFile from '@/components/uploadFile'

export default {
  components: {
    UploadFile,
    DataTable,
    TableTop,
    TableBottom,
    SelectWithSearch,
    DatePicker
  },
  computed: {
    ...mapGetters({
      previewUploadBLU: 'BargeLineUp/previewUploadBLU',
      uploadStatus: 'UploadFile/status',
      fileName: 'UploadFile/fileName',
      isUploadError: 'UploadFile/isUploadError',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      search: 'Table/search',
      dropdownOptions: 'BargeLineUp/dropdownOptionsBLU',
      bargeLineUpUploadColumnConfig: 'BargeLineUp/bargeLineUpUploadColumnConfig'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      if (this.tableData.length) {
        if (this.search) {
          const tempSearchedData = this.tableData.filter(row =>
            Object.values(row).some(val =>
              String(val.value).toLowerCase().includes(this.search.toLowerCase())
            ))

          return tempSearchedData.slice(start, end)
        }
        return this.tableData.slice(start, end);
      }
      return []
    },
    comErrorUnsynchronizedVLU() {
      if (this.selectedRow.length) {
        return this.result.errorUnsynchronizedVLU.filter(v => this.selectedRow.includes(v))
      }
      return this.result.errorUnsynchronizedVLU
    },
    comErrorCombinationOngoing() {
      if (this.selectedRow.length) {
        return this.result.errorCombinationOngoing.filter(v => this.selectedRow.includes(v))
      }
      return this.result.errorCombinationOngoing
    },
    comErrorCompletedVLU() {
      if (this.selectedRow.length) {
        return this.result.errorCompletedVLU.filter(v => this.selectedRow.includes(v))
      }
      return this.result.errorCompletedVLU
    },
    comError() {
      if (this.selectedRow.length) {
        return this.result.error.filter(v => this.selectedRow.includes(v.row))
      }
      return this.result.error
    },
    comIsError() {
      if (this.selectedRow.length) {
        return this.result.error.length && this.result.error.some(v => this.selectedRow.includes(v.row))
      }
      return this.result?.error?.length !== 0
    }
  },
  watch: {
    previewUploadBLU: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.result.isError = val.is_error
          this.result.error = val.error
          this.result.errorCombinationOngoing = val.errorCombinationOngoing
          this.result.errorCompletedVLU = val.errorCompletedVLU
          this.result.errorUnsynchronizedVLU = val.errorUnsynchronizedVLU
          this.result.errorExist = val.errorExist

          this.populateDataTable(val.data)
        }

        this.isTableLoading = false
      }
    },
    search: {
      deep: true,
      handler(val) {
        if (val) {
          this.searchedData = this.tableData.filter(row =>
            Object.values(row).some(data =>
              String(data.value).toLowerCase().includes(val.toLowerCase())
            ))

          this.$store.commit('Table/SET_TOTAL_DATA', this.searchedData.length, { root: true })
          this.$store.commit('Table/SET_TOTAL_PAGE', Math.ceil(this.searchedData.length / this.pageSize), { root: true })
        } else {
          this.$store.commit('Table/SET_TOTAL_DATA', this.tableData.length, { root: true })
          this.$store.commit('Table/SET_TOTAL_PAGE', Math.ceil(this.tableData.length / this.pageSize), { root: true })
        }
      }
    },
    bargeLineUpUploadColumnConfig: {
      immediate: true,
      handler(val) {
        if (val) {
          this.columnStart = val.column_start || ''
          this.columnEnd = val.column_end || ''
        }
      }
    }
  },
  data() {
    return {
      columnStart: '',
      columnEnd: '',
      isProcessing: false,
      isLoading: false,
      isTableLoading: false,
      tableHeaders: [
        { key: 'master_checkbox', label: 'checkbox' },
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action' },
        { key: 'name_of_vessel', label: 'Vessel Name', sortable: true },
        { key: 'end_user', label: 'End User', sortable: true },
        { key: 'term', label: 'Term', sortable: true },
        { key: 'tug', label: 'Tugboat Name', sortable: true },
        { key: 'barge', label: 'Barge Name', sortable: true },
        { key: 'fleet_id', label: 'Fleet ID', sortable: true },
        { key: 'route', label: 'Route', sortable: true },
        { key: 'loading_date', label: 'Loading Date', width: '170px', sortable: true },
        { key: 'max_cargo', label: 'Max. Cargo', sortable: true },
        { key: 'grt', label: 'GT Barge', sortable: true },
        { key: 'pol', label: 'POL', sortable: true },
        { key: 'pod', label: 'POD', sortable: true },
        { key: 'shipper_name', label: 'Shipper', sortable: true },
        { key: 'type_export', label: 'Export/Domestic', sortable: true },
        { key: 'trip_number', label: 'Trip No.', sortable: true },
        { key: 'product_id', label: 'Product ID', sortable: true },
        { key: 'cargo_demand', label: 'Cargo Demand', sortable: true },
        { key: 'trip_start', label: 'Trip Start', width: '210px', sortable: true },
        { key: 'trip_end', label: 'Trip End', width: '210px', sortable: true },
        { key: 'barge_cycle_days', label: 'Barge Cycle (Days)', sortable: true },
        { key: 'arrive_smds', label: 'Arrive SMDS', width: '210px', sortable: true },
        { key: 'depart_smds', label: 'Depart SMDS', width: '210px', sortable: true },
        { key: 'arrived_pol', label: 'Arrive POL', width: '210px', sortable: true },
        { key: 'loading_start', label: 'Loading Start', width: '210px', sortable: true },
        { key: 'loading_end', label: 'Loading End', width: '210px', sortable: true },
        { key: 'cast_off', label: 'Cast Off', width: '210px', sortable: true },
        { key: 'departure', label: 'Departure', width: '210px', sortable: true },
        { key: 'smd_for_bridge', label: 'SMD for Bridge', width: '210px', sortable: true },
        { key: 'open_bridge', label: 'Open Bridge', width: '210px', sortable: true },
        { key: 'actual_passing_bridge', label: 'Actual Passing Bridge', width: '210px', sortable: true },
        { key: 'arrived_pod', label: 'Arrive POD', width: '210px', sortable: true },
        { key: 'unloading_start', label: 'Unloading Start', width: '210px', sortable: true },
        { key: 'unloading_end', label: 'Unloading End', width: '210px', sortable: true },
        { key: 'cast_off_2', label: 'Cast Off', width: '210px', sortable: true },
        { key: 'departure_2', label: 'Departure', width: '210px', sortable: true },
        { key: 'arrive_smdr', label: 'Arrive SMDR', width: '210px', sortable: true },
        { key: 'remark', label: 'Remark', sortable: true }
      ],
      tableData: [],
      groupHeader: [],
      usedColors: new Set(),
      productKeyList: [],
      result: {
        isError: false,
        errorUnsynchronizedVLU: [],
        errorCombinationOngoing: [],
        errorCompletedVLU: [],
        errorExist: [],
        error: []
      },
      searchedData: [],
      termOptions: [
        { value: 'TC', text: 'TC' },
        { value: 'SPOT', text: 'SPOT' },
        { value: 'FAS', text: 'FAS' }
      ],
      typeExportOptions: [
        { value: 'EXPORT', text: 'Export' },
        { value: 'DOMESTIC', text: 'Domestic' }
      ],
      tugOptions: [],
      bargeOptions: [],
      fleetOptions: [],
      polOptions: [],
      podOptions: [],
      shipperOptions: [],
      masterDataOptions: {
        tug: [],
        barge: [],
        fleet_id: [],
        pol: [],
        pod: [],
        shipper: []
      },
      isLoadingRefresh: false,
      selectedRow: []
    }
  },
  created() {
    this.fetchColumnConfig()
  },
  methods: {
    async fetchColumnConfig() {
      try {
        await this.$store.dispatch('BargeLineUp/getBargeLineUpUploadColumnConfig')
      } catch (e) {
        // silent fail, fallback handled by watcher defaulting to empty strings
      }
    },
    populateTripNumberError(row) {
      if(!row.editForm.trip_number.value) {
        return true
      } else {
        const allTripNumber = this.tableData.filter(item => item.row !== row.row).map(td => (td.trip_number.value))
        if(allTripNumber.includes(row.editForm.trip_number.value)) {
          this.showFailedNotification(this, 'Trip Number must be unique')
          return true
        }
      }
    },
    populateDataTable(val) {
      if (this.isEmpty(val)) return

      this.tableData = this.cloneDeep(val.map(row => ({
        ...row,
        isWarning: this.hasWarning(row)
      })))

      const tempProductData = this.cloneDeep(val).map(v => ({
        row: v.row,
        ...this.groupByCompany(v.product_data)
      }))

      this.groupHeader = Object.entries(tempProductData[0]).map(([company, items]) => {
        const keys = Object.keys(items)
        return {
          title: company === '3rd_party' ? '3RD Party' : company.toUpperCase(),
          startColumn: keys[0],
          endColumn: keys[keys.length - 1],
          backgroundColor: this.getRandomColor()
        }
      })

      this.productKeyList = Object.values(tempProductData[0]).flatMap(items =>
        Object.entries(items).map(([key, { title }]) => ({
          key,
          label: title
        }))
      )
          
      this.tableHeaders.splice(
        19,
        0,
        ...this.productKeyList.map(v => ({
          ...v,
          sortable: true
        }))
      )
    },
    resetColumns() {
      this.columnStart = null
      this.columnEnd = null
    },
    async handleUploadFile(file) {
      this.isTableLoading = true
      try {
        await this.$store.dispatch('BargeLineUp/uploadBargeLineUp', file)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to upload excel, please try again')
      }
    },
    groupByCompany(data) {
      const grouped = {};

      Object.entries(data).forEach(([key, obj]) => {
        const normalizedKey = this.normalizeKey(key)
        const normalizeKeyCompany = this.normalizeKey(obj.company)
        if (!grouped[normalizeKeyCompany]) {
          grouped[normalizeKeyCompany] = {};
        }
        grouped[normalizeKeyCompany][normalizedKey] = {
          title: key,
          value: obj.value
        }
      });

      return grouped;
    },
    normalizeKey(key) {
      return key.toLowerCase().replace(/[-. ]/g, "_")
    },
    postBLU() {},
    async saveColumnConfig() {
      try {
        this.isProcessing = true
        this.changeButtonState(this.$refs.saveConfigButton.$el)

        await this.$store.dispatch('BargeLineUp/postBargeLineUpConfig',
          { column_start: this.columnStart, column_end: this.columnEnd })

        this.isProcessing = false
        this.changeButtonState(this.$refs.saveConfigButton.$el)
        this.showSuccessNotification(this,'You have successfully save configuration')
      } catch ({ error }) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.saveConfigButton.$el)
        this.showFailedNotification(this, error.message)
      }
    },
    onHandleCancel(row) {
      // Just cancel the edit
      const index = this.tableData.findIndex(item => item.row === row.row)
      if (index !== -1) {
        this.tableData[index].isEditing = false
        this.$set(this.tableData[index], 'isEditing', false)
      }
    },
    async onHandleEdit(row) {
      // Cancel any existing edit or add
      if (this.tableData.some(r => r.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      this.onHandleSelectAll(false)

      const optionsFields = ['BARGE', 'TUG', 'FLEET_ID', 'POL', 'POD', 'SHIPPER']
      await Promise.all(optionsFields.map(field => this.fetchDropdownByType(field, row.term.value, row.fleet_id.value)))

      // Populate Edit Form
      const index = this.tableData.findIndex(item => item.row === row.row)
      const { editForm, ...dataRow } = row
      if (index !== -1) {
        this.$set(this.tableData[index], 'isEditing', true)
        this.$set(this.tableData[index], 'editForm', { ...this.cloneDeep(dataRow), tug: { value: dataRow.tug.value } })

        const dateFields = [
          'loading_date', 'trip_start', 'trip_end', 'arrive_smds', 'depart_smds',
          'arrived_pol', 'loading_start', 'loading_end', 'cast_off', 'departure',
          'smd_for_bridge', 'open_bridge', 'actual_passing_bridge', 'arrived_pod',
          'unloading_start', 'unloading_end', 'cast_off_2', 'departure_2', 'arrive_smdr'
        ]

        dateFields.forEach(field => {
          const format = field === 'loading_date' ? 'DD-MM-YYYY' : undefined
          const val = row[field]?.value
          this.tableData[index].editForm[field].value = val
            ? this.moment(val, format).toDate()
            : null
        })
      }
    },
    getRandomColor(alpha = 1) {
      let color;
      do {
        const r = Math.floor(Math.random() * 156) + 100;
        const g = Math.floor(Math.random() * 156) + 100;
        const b = Math.floor(Math.random() * 156) + 100;
        color = `rgba(${r},${g},${b},${alpha})`;
      } while (this.usedColors.has(color));

      this.usedColors.add(color);
      return color;
    },
    rowClass(row) {
      if (this.hasDisableRow(row)) {
        return 'blu-row-error'
      }
      if (this.hasExist(row)) {
        return 'blu-row-warning'
      }
      return ''
    },
    hasWarning(row) {
      const validate = Object.values(row).some(
        val => val && typeof val === 'object' && ((val.is_warning && val.is_warning === true) || (val.is_error && val.is_error === true))
      ) ||
      (row.product_data &&
        Object.values(row.product_data).some(p => p.value === null || p.value < 0))
      return validate
    },
    hasDisableRow(row) {
      return [
        ...this.result.errorUnsynchronizedVLU,
        ...this.result.errorCombinationOngoing,
        ...this.result.errorCompletedVLU
      ].includes(row.row)
    },
    hasExist(row) {
      return this.result.errorExist.includes(row.row)
    },
    changePageSize() {
      const dataLength = this.search ? this.searchedData.length : this.tableData.length
      this.$store.commit('Table/SET_TOTAL_PAGE', Math.ceil(dataLength / this.pageSize), { root: true })
    },
    goToRowTable(row) {
      this.$store.commit('Table/SET_SEARCH', null)
      this.$refs.pagination.goToRow(row)
    },
    onHandleRefresh(type, row) {
      this.isLoadingRefresh = true
      if (type === 'BARGE') {
        this.bargeOptions = []
      }
      if (type === 'TUG') {
        this.tugOptions = []
      }
      if (type === 'POD') {
        this.podOptions = []
      }
      if (type === 'POL') {
        this.polOptions = []
      }
      if (type === 'SHIPPER') {
        this.shipperOptions = []
      }
      if (type === 'FLEET_ID') {
        this.fleetOptions = []
      }

      this.fetchDropdownByType(type, row.editForm.term.value, row.fleet_id.value)
    },
    onHandleLink(url, query = null) {
      const routeConfig = { name: url }

      if (query) {
        routeConfig.query = { tab: query }
      }

      const routeData = this.$router.resolve(routeConfig)

      window.open(routeData.href, "_blank")
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    },
    validateAndFormatDate(date, format) {
      // every falsy data transformed into '-'
      if (date && date !== '-') {
        return this.moment(
          date,
          [
            'DD-MM-YYYY',
            'YYYY-MM-DDTHH:mm.SSSSZ'
          ]
        ).format(format)
      }
      return '-'
    },
    async fetchDropdownOptionsBLU(filter) {
      await this.$store.dispatch('BargeLineUp/getDropdownOptionsBLU', { ...filter }) // filter = {search, term, type}
    },
    async fetchDropdownByType(type, term, fleetId) {
      try {
        await this.fetchDropdownOptionsBLU(
          {
            ...(term && {term}),
            type
          }
        )

        const options = this.dropdownOptions || []

        switch (type) {
          case 'BARGE':
            this.masterDataOptions.barge = options
            this.bargeOptions = options.map(val => ({ value: val.barge, text: val.barge, id: val.barge_id }))
            this.isLoadingRefresh = false
            return
          case 'TUG':
            this.masterDataOptions.tug = options
            this.tugOptions = options.map(val => ({ value: val.tug, text: val.tug, id: val.tug_id }))
            this.isLoadingRefresh = false
            return
          case 'POD':
            this.masterDataOptions.pod = options
            this.podOptions = options.map(val => ({ value: val.name, text: val.name, id: val.id}))
            this.isLoadingRefresh = false
            return
          case 'POL':
            this.masterDataOptions.pol = options
            this.polOptions = options.map(val => ({ value: val.name, text: val.name, id: val.id}))
            this.isLoadingRefresh = false
            return
          case 'SHIPPER':
            this.masterDataOptions.shipper = options
            this.shipperOptions = options.map(val => ({ value: val.code, text: val.code, id: val.id }))
            this.isLoadingRefresh = false
            return
          case 'FLEET_ID':
            this.masterDataOptions.fleet_id = options
            this.fleetOptions = options.map(val => ({ value: val.fleet_id, text: val.fleet_id, id: val.id }))
            this.isLoadingRefresh = false
            return
          default:
            return
        }
      } catch {
        return
      }
    },
    async onTermChange(row, isBargeChange = false) {
      if (!isBargeChange) {
        row.editForm.barge.value = null
        row.editForm.barge.is_error = !row.editForm.barge.value
        row.editForm.barge.is_warning = !row.editForm.barge.value
      }
      row.editForm.tug.value = null
      row.editForm.tug.is_error = !row.editForm.tug.value
      row.editForm.tug.is_warning = !row.editForm.tug.value
      row.editForm.fleet_id.value = null

      const optionsFields = ['BARGE', 'TUG', 'FLEET_ID']
      await Promise.all(optionsFields.map(field => this.fetchDropdownByType(field, row.editForm.term.value, row.fleet_id.value)))
      row.editForm.term.is_error = !row.editForm.term.value
      row.editForm.term.is_warning = !row.editForm.term.value
    },
    async onRelationChange(row, field) {
      const excludedRow = this.tableData.filter(item => item.row !== row.row)
      const allTugData = excludedRow.map(item => item.tug.value).filter(item => item !== null)
      const allBargeData = excludedRow.map(item => item.barge.value).filter(item => item !== null)
      const allFleetData = excludedRow.map(item => item.fleet_id.value).filter(item => item !== null)
      if (row.editForm?.barge?.value?.toLowerCase()?.includes('spot')) {
        row.editForm.term.value = 'SPOT'

        this.onTermChange(row, true)
        return
      }
      if (row.editForm.term.value !== 'TC') {
        row.editForm.barge.is_error = allBargeData.includes(row.editForm.barge.value) || !row.editForm.barge.value
        row.editForm.barge.is_warning = allBargeData.includes(row.editForm.barge.value) || !row.editForm.barge.value
        row.editForm.tug.is_error = allTugData.includes(row.editForm.tug.value) || !row.editForm.tug.value
        row.editForm.tug.is_warning = allTugData.includes(row.editForm.tug.value) || !row.editForm.tug.value
        row.editForm.fleet_id.is_error = allFleetData.includes(row.editForm.fleet_id.value) && row.editForm.fleet_id.value
        return
      }

      const sourceData = this.masterDataOptions[field]?.find(
        v => v[field] === row.editForm[field].value
      )

      if (!sourceData) return

      row.editForm.grt.value = sourceData.gt_barge
      row.editForm.max_cargo.value = sourceData.max_cargo

      row.editForm.barge.value = sourceData.barge
      row.editForm.barge.is_error = allBargeData.includes(sourceData.barge) || !sourceData.barge
      row.editForm.barge.is_warning = allBargeData.includes(sourceData.barge) || !sourceData.barge
      row.editForm.tug.value = sourceData.tug
      row.editForm.tug.is_error = allTugData.includes(sourceData.tug) || !sourceData.tug
      row.editForm.tug.is_warning = allTugData.includes(sourceData.tug) || !sourceData.tug
      row.editForm.fleet_id.value = sourceData.fleet_id
      row.editForm.fleet_id.is_error = allFleetData.includes(sourceData.fleet_id) && sourceData.fleet_id
    },
    onPolPodChange(row) {
      row.editForm.pol.is_error = !row.editForm.pol.value
      row.editForm.pol.is_warning = !row.editForm.pol.value
      row.editForm.pod.is_error = !row.editForm.pod.value
      row.editForm.pod.is_warning = !row.editForm.pod.value

      row.editForm.route.value = `${row.editForm.pol.value} - ${row.editForm.pod.value}`
    },
    async onHandleValidateRow(row) {
      try {
        const { editForm } = row
        const { isWarning, is_checked, isEditing, ...tempEditForm } = editForm
        const errorKeys = Object.keys(editForm).filter(key => editForm[key].is_error || editForm[key].is_warning);
        if(errorKeys.length) {
          this.showFailedNotification(this, `Error found in: ${errorKeys.join(', ')} field`, 'Error')
          return
        }
        // Transform + format date
        const transformData = this.transformRow(tempEditForm)
        transformData.loading_date = this.validateAndFormatDate(transformData.loading_date, 'DD-MM-YYYY')

        // Validate row from API
        const data = await this.$store.dispatch('BargeLineUp/postValidateRow', transformData)

        if (data.is_disabled) {
          this.showFailedNotification(
            this,
            'BLU with this combination already exists and already ongoing or completed. Please change your tug, barge or loading date.'
          )
          return
        }

        this.updateErrorExist(data, row)
        this.updateErrors(data, row)

        this.showSuccessNotification(this, `BLU Row ${row.row} successfully validated.`)

        this.refreshRow(row, data.data[0])
      } catch ({ error }) {
        const message =
          error?.errors?.[0]?.[0]?.message ||
          error?.message ||
          'Failed to validate, please try again'

        this.showFailedNotification(this, message, 'Error')
      }
    },
    updateErrorExist(data, row) {
      const idx = this.result?.errorExist?.findIndex(v => v === row.row)
      if (idx >= 0 && data.errorExist?.length) {
        this.result.errorExist.splice(idx, 1)
      }
      if (data.errorExist?.[0] && !this.result.errorExist.includes(data.errorExist[0])) {
        this.result.errorExist.push(data.errorExist[0])
      }
    },
    updateErrors(data, row) {
      const idx = this.result?.error?.findIndex(v => v.row === row.row)
      if (data.error.length) {
        data.error.forEach(v => {
          if (idx >= 0) {
            this.result.error[idx] = { ...v, row: row.row }
          } else {
            this.result.error.push({ ...v, row: row.row })
            this.result.error.sort((a, b) => a.row - b.row)
          }
        })
      } else if (idx >= 0) {
        this.result.error.splice(idx, 1)
      }
    },
    refreshRow(row, newData) {
      const idx = this.tableData.findIndex(v => v.row === row.row)
      if (idx >= 0) {
        this.tableData.splice(idx, 1, {
          ...newData,
          row: row.row,
          isWarning: this.hasWarning(newData)
        })
      }

      // Force UI re-render
      const clone = this.cloneDeep(this.tableData)
      this.tableData = []
      setTimeout(() => {
        this.tableData = clone
      }, 500)
    },
    async onHandleGenerate() {
      this.isProcessing = true
      this.changeButtonState(this.$refs.submitButton.$el)

      let selectedData = !this.selectedRow.length ? this.tableData : this.tableData.filter(v => this.selectedRow.includes(v.row))

      selectedData = selectedData.map(v => {
        const { isEditing, editForm, ...data } = v
        return this.transformRow(data)
      })

      try {
        const data = await this.$store.dispatch('BargeLineUp/postGenerateBLU', selectedData)
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showSuccessNotification(this,'You have successfully generate BLU data')

        this.$store.commit('BargeLineUp/SET_PREVIEW_UPLOAD_BLU', [])
        this.resetData()

        this.$router.push({
          name: 'BargeLineUpUploadResult',
          query: { n: data.createdData ?? 0 }
        });
      } catch ({ data }) {
        const { error } = data
        
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        const message = error?.errors?.[0]?.[0]?.message || error?.message || 'Failed to generate BLU data, please try again'

        this.showFailedNotification(this, message, 'Error')
      }
    },
    transformRow(row) {
      const output = { row: row.row, product_data: row.product_data }

      Object.keys(row).forEach(key => {
        if (key === 'row' || key === 'product_data') return

        const field = row[key]

        if (field && typeof field === 'object' && 'value' in field) {
          output[key] = field.value
        } else {
          output[key] = field
        }
      })

      return output
    },
    onHandleSelectAll(is_check) {
      if (is_check) {
        if (this.search) {
          this.selectedRow = this.searchedData.filter(v => !this.hasDisableRow(v)).map(d => d.row)
          this.searchedData.forEach(v => {
            if (!this.hasDisableRow(v)) {
              v.is_checked = true
            }
          })
          return
        }
        this.selectedRow = this.tableData.filter(v => !this.hasDisableRow(v)).map(d => d.row)
        this.tableData.forEach(v => {
          if (!this.hasDisableRow(v)) {
            v.is_checked = true
          }
        })
      } else {
        this.selectedRow = []
        this.tableData.forEach(v => {
          if (!this.hasDisableRow(v)) {
            v.is_checked = false
          }
        })
      }
    },
    resetData() {
      this.result = this.$options.data().result
      this.tableHeaders = this.$options.data().tableHeaders
      this.groupHeader = []
      this.productKeyList = []
      this.searchedData = []
      this.selectedRow = []
      this.tableData = []
      this.$store.dispatch('Table/resetConfig')
    },
    validateValue(val) {
      return val?.value ?? '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.config-container {
  @apply flex flex-row p-5 my-3 bg-white rounded-lg;
}
.text-alert-success {
  color: #207032 !important;
  background-color: rgba(43, 172, 71, .3);
}
.text-alert-danger {
  color: #B01E47 !important;
  background-color: rgba(176, 30, 71, .3);
}
.collapse-container {
  max-height: 250px !important;
  overflow-y: auto;
}
.is-not-valid {
  /deep/ .vs-con-input,
  /deep/ .mx-input-wrapper,
  /deep/ .bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}
</style>
